import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import {
    DataGrid,
    GridCell,
    GridCellProps,
    GridLogicOperator,
    GridSortCellParams,
    gridClasses,
    gridNumberComparator,
    gridStringOrNumberComparator,
} from '@mui/x-data-grid';
import { differenceInMinutes } from 'date-fns';
import { capitalize, isNil } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMixpanel } from 'react-mixpanel-browser';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_CREDIT_USAGE_HISTORY_DAYS, useSatelliteCreditUsageStats } from '@/api/credits';
import { useDeviceIdList } from '@/api/deviceIdList';
import { Device, useDevices } from '@/api/devices';
import { useActiveOtaPlans } from '@/api/ota/useDeviceOtaDetail';
import { SubscriptionTagInfo, telemetryTypeToLabelMap, useSubscriptionTagsInfo } from '@/api/subscriptionTags';
import { useSubscriptionsInfo } from '@/api/subscriptions';
import { useCreditBalance, useUserAccount } from '@/api/userAccount';
import { SuccessIcon } from '@/components/StatusIcons';
import { Timestamp } from '@/components/data/Timestamp';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { DeviceTelemetryTransactionManagement } from '@/components/subscriptions/DeviceTelemetryTransactionManagement';
import { Spacer } from '@/components/util';
import { LineType, useTelemetryLineChanges } from '@/context/transaction/TelemetryLineChangeContext';
import { useSpotterHash } from '@/hooks/useSpotterHash';
import { minorTextGrey } from '@/theme';
import { showUiElementPerIridiumVar } from '@/utils';
import { itemQuantity } from '@/utils/languageHelpers';

export const CREDIT_USAGE_WINDOW_DAYS = 30;

const OTA_IN_PROGRESS_TOOLTIP = "This system's telemetry cannot be changed while the system is updating";

const LINE_TRANSITION_STATES: readonly string[] = [
    'ACTIVATING',
    'DEACTIVATING',
    'RESUMING',
    'SUSPENDING',
    'CHANGING',
] as const;

export const DeviceTelemetryManagementTable = () => {
    const mixpanel = useMixpanel();
    const { spotterId: hashSpotterId } = useSpotterHash();
    const navigate = useNavigate();

    const { data: deviceIdItems } = useDeviceIdList();
    const { data: devices, isPending: devicesPending } = useDevices({
        devices: deviceIdItems?.data?.devices ?? [],
    });
    const { data: subscriptionTagsInfo } = useSubscriptionTagsInfo();
    const activeOtaPlans = useActiveOtaPlans(subscriptionTagsInfo?.data.map(({ spotter_id }) => spotter_id) ?? []);
    const { data: subscriptionInfo } = useSubscriptionsInfo();
    const { data: accountData } = useUserAccount();
    const accountIridiumVar = accountData?.data?.user?.accountIridiumVar;
    const varSupportsDisabling = showUiElementPerIridiumVar(
        accountIridiumVar,
        'varSupportsDisablingSatelliteTelemetry',
        'every'
    );
    const isUsingSatelliteCredits = showUiElementPerIridiumVar(accountIridiumVar, 'usesSatelliteCredits');
    const isPastDue = !!subscriptionInfo?.data.length && subscriptionInfo.data[0].status === 'past_due';

    const [isManageMode, setManageMode] = useState(false);
    const searchboxRef = useRef<HTMLDivElement>(null);
    const [isSearchboxExpanded, setSearchboxExpanded] = useState(!!hashSpotterId);
    const [searchText, setSearchText] = useState(hashSpotterId ?? '');
    const quickFilterValues = useMemo(() => {
        if (!searchText) return undefined;

        return searchText.toLowerCase().split(/\s/);
    }, [searchText]);

    const { data: { spotterUsage: spotterSatelliteUsage } = {}, isPending: satelliteUsagePending } =
        useSatelliteCreditUsageStats();

    useEffect(() => {
        // if our hash is no longer accurate, clear it out. not bothering to keep it populated,
        // the hash is really just for helping people find a specific spotter when arriving via link.
        if (hashSpotterId && searchText !== hashSpotterId) {
            navigate({ hash: '' }, { replace: true });
        }
    }, [searchText, hashSpotterId, navigate]);

    const rows = useMemo(() => {
        if (!subscriptionTagsInfo?.data) return [];

        return subscriptionTagsInfo?.data.map((subTagsInfo) => {
            const device = devices?.find(({ spotterId }) => spotterId === subTagsInfo.spotter_id);
            const isOtaInProgress = subTagsInfo.spotter_id in activeOtaPlans;

            return {
                ...subTagsInfo,
                displayName: subTagsInfo.spotter_name || subTagsInfo.spotter_id, // deliberately `||` not `??` because sometimes they're empty
                device,
                satelliteLine: collectLineFields('iridium', varSupportsDisabling, isOtaInProgress, subTagsInfo, device),
                cellularLine: collectLineFields('cellular', true, isOtaInProgress, subTagsInfo, device),
                satelliteUsage: spotterSatelliteUsage?.[subTagsInfo.spotter_id],
                ...(device && {
                    lastCheckin:
                        device.lastMessageTime ??
                        (device.timeLastUpdatedUTC ? new Date(device.timeLastUpdatedUTC).getTime() : null),
                }),
            };
        });
    }, [activeOtaPlans, devices, spotterSatelliteUsage, subscriptionTagsInfo?.data, varSupportsDisabling]);

    return (
        <Stack sx={{ gap: 2, maxHeight: 'calc(100vh - 60px)' }}>
            <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                {!isSearchboxExpanded && (
                    <Button data-testid="searchbox-expand-button" onClick={() => setSearchboxExpanded(true)}>
                        <SearchIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Search
                    </Button>
                )}
                {isSearchboxExpanded && (
                    <TextField
                        autoFocus
                        data-testid="searchbox-input"
                        ref={searchboxRef}
                        value={searchText}
                        size="small"
                        onChange={(event) => setSearchText(event.target.value)}
                        onBlur={() => {
                            if (!searchText) {
                                setSearchboxExpanded(false);
                            }
                        }}
                        sx={{ visibility: isSearchboxExpanded ? 'visible' : 'collapse', my: '-2px' }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ p: 0, ml: '-6px' }} color="primary" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            sx={{
                                                ...(!searchText && { visibility: 'hidden', pointerEvents: 'none' }),
                                                p: 0,
                                                mr: '-4px',
                                            }}
                                            onClick={() => {
                                                setSearchText('');
                                                setSearchboxExpanded(false);
                                            }}
                                        >
                                            <CancelIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                )}
                <Spacer />
                {!isManageMode && (
                    <>
                        <Button variant="outlined" href="/setup/register">
                            Register Spotter
                        </Button>
                        <Button
                            data-testid="telemetry-manage-button"
                            variant="contained"
                            disabled={!subscriptionTagsInfo?.data.length || !subscriptionInfo?.data || isPastDue}
                            onClick={() => {
                                mixpanel?.track('Telemetry management mode entered');
                                setManageMode(true);
                            }}
                        >
                            Change telemetry lines
                        </Button>
                    </>
                )}
            </Stack>
            <DataGrid
                pageSizeOptions={[100]}
                getRowId={({ device_id }) => device_id}
                getRowHeight={() => 'auto'}
                getRowClassName={({ indexRelativeToCurrentPage }) =>
                    `deviceTelemetryRow ${indexRelativeToCurrentPage % 2 ? 'even' : 'odd'}`
                }
                getCellClassName={() => 'deviceTelemetryCell'}
                disableRowSelectionOnClick={true}
                sx={{
                    borderRadius: 0,
                    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within,
                        & .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                        outline: 'none',
                    },
                    [`.${gridClasses.columnHeader}`]: { bgcolor: grey[200], px: 2, fontSize: '1rem' },
                    '.deviceTelemetryCell': { display: 'flex', alignItems: 'center', p: 2 },
                    '.deviceTelemetryRow.odd:hover': { bgcolor: 'unset' },
                    '.deviceTelemetryRow.even': { bgcolor: grey[50] },
                }}
                showCellVerticalBorder={true}
                showColumnVerticalBorder={true}
                filterModel={{ items: [], quickFilterValues, quickFilterLogicOperator: GridLogicOperator.Or }}
                columnVisibilityModel={{
                    satelliteUsage: isUsingSatelliteCredits,
                }}
                slots={{
                    cell: (props: GridCellProps) => {
                        return <GridCell data-testid={`telemetry-cell-${props.column.field}`} {...props} />;
                    },
                }}
                initialState={{ sorting: { sortModel: [{ field: 'displayName', sort: 'asc' }] } }}
                columns={[
                    {
                        field: 'displayName',
                        headerName: 'Device',
                        flex: 1,
                        minWidth: 160,
                        hideable: false,
                        disableColumnMenu: true,
                        sortingOrder: ['asc', 'desc'],
                        getApplyQuickFilterFn: (searchTerm?: string) => {
                            if (!searchTerm) return null;

                            return (_value, { spotter_id, spotter_name, displayName }) =>
                                spotter_id?.toLowerCase().includes(searchTerm) ||
                                spotter_name?.toLowerCase().includes(searchTerm) ||
                                displayName?.toLowerCase().includes(searchTerm);
                        },
                        renderCell: ({ row: { spotter_id, displayName } }) => (
                            <Stack sx={{ alignItems: 'start' }}>
                                <Button
                                    data-testid="device-name-link"
                                    variant="text"
                                    href={`/spotter/${spotter_id}`}
                                    sx={{ p: 0, justifyContent: 'start', minWidth: 0, userSelect: 'auto' }}
                                >
                                    <Typography variant="h4" sx={{ wordBreak: 'break-all', maxWidth: 1 }}>
                                        {displayName}
                                    </Typography>
                                </Button>
                                {displayName !== spotter_id && (
                                    <Typography
                                        variant="body1"
                                        sx={{ color: minorTextGrey }}
                                        data-testid="device-spotid-subtext"
                                    >
                                        {spotter_id}
                                    </Typography>
                                )}
                            </Stack>
                        ),
                    },
                    {
                        field: 'cellularLine',
                        headerName: 'Cellular line',
                        flex: 0.8,
                        minWidth: 160,
                        hideable: false,
                        disableColumnMenu: true,
                        sortingOrder: ['desc', 'asc'],
                        renderCell: ({ value }) => (
                            <DeviceTelemetryCell editMode={isManageMode} lineStatus={value} lineType="cellular" />
                        ),
                        getSortComparator: getLineStatusSortComparator,
                    },
                    {
                        field: 'satelliteLine',
                        headerName: 'Satellite line',
                        flex: 0.8,
                        minWidth: 160,
                        hideable: false,
                        disableColumnMenu: true,
                        sortingOrder: ['desc', 'asc'],
                        getSortComparator: getLineStatusSortComparator,
                        renderCell: ({ value }) => (
                            <DeviceTelemetryCell editMode={isManageMode} lineStatus={value} lineType="iridium" />
                        ),
                    },
                    {
                        field: 'satelliteUsage',
                        headerName: 'Satellite usage',
                        width: 170,
                        hideable: false,
                        disableColumnMenu: true,
                        sortingOrder: ['desc', 'asc'],
                        getApplyQuickFilterFn: () => null,
                        getSortComparator: (sortDirection) => {
                            const modifier = sortDirection === 'desc' ? -1 : 1;
                            return (
                                v1: (typeof rows)[number]['satelliteUsage'],
                                v2: (typeof rows)[number]['satelliteUsage'],
                                p1,
                                p2
                            ) => {
                                // regardless of sort order, put devices with no usage last
                                if (isNil(v2)) {
                                    return -1;
                                }
                                if (isNil(v1)) {
                                    return 1;
                                }

                                // sort by average daily usage so a recently-enabled big user comes to the top
                                return (
                                    modifier *
                                    gridStringOrNumberComparator(
                                        v1.usageAmount / v1.usageDays,
                                        v2.usageAmount / v2.usageDays,
                                        p1,
                                        p2
                                    )
                                );
                            };
                        },
                        renderCell: ({ row: { satelliteUsage } }) =>
                            satelliteUsagePending ? (
                                <GenericSkeleton lines={1} sx={{ width: 1 }} />
                            ) : (
                                <Stack>
                                    <Typography variant="h5">{satelliteUsage?.usageAmount ?? 0} credits</Typography>
                                    <Typography variant="minorLabel">
                                        Last{' '}
                                        {itemQuantity(
                                            satelliteUsage?.usageDays ?? DEFAULT_CREDIT_USAGE_HISTORY_DAYS,
                                            'day'
                                        )}
                                    </Typography>
                                </Stack>
                            ),
                    },
                    {
                        field: 'lastCheckin',
                        headerName: 'Last checked in',
                        width: 176,
                        hideable: false,
                        disableColumnMenu: true,
                        sortingOrder: ['desc', 'asc'],
                        getApplyQuickFilterFn: () => null,
                        renderCell: ({ value }) =>
                            devicesPending ? (
                                <GenericSkeleton lines={1} sx={{ width: 1 }} />
                            ) : (
                                <Timestamp variant="h5" value={value} unit="date" type="medium" fallback="" />
                            ),
                    },
                ]}
                rows={rows}
            ></DataGrid>
            <>
                {createPortal(
                    <DeviceTelemetryTransactionManagement
                        isManageMode={isManageMode}
                        onEndManagement={() => setManageMode(false)}
                    />,
                    document.body,
                    'device-telemetry-transaction-management'
                )}
            </>
        </Stack>
    );
};

const getLineStatusSortComparator =
    (sortDirection?: 'asc' | 'desc' | null) =>
    (v1: LineStatusFields, v2: LineStatusFields, p1: GridSortCellParams, p2: GridSortCellParams) => {
        const modifier = sortDirection === 'desc' ? -1 : 1;

        // regardless of sort order, put devices that can't use this telemetry type at the bottom
        if (!v2.isAllowed || !v2.hasHardware || !v2.varSupportsDisabling) {
            return -1;
        }
        if (!v1.isAllowed || !v1.hasHardware || !v1.varSupportsDisabling) {
            return 1;
        }

        return modifier * gridNumberComparator(v1.shouldBeActive, v2.shouldBeActive, p1, p2);
        // TODO: https://app.shortcut.com/sofar/story/242139 once we have last modified timestamp, use that to break ties on shouldBeActive
        // target order: when 'desc' (on first sort click): enableds by descending last modified, disableds by descending last modified, incapable devices last
        //               when 'asc' (on second sort click): disableds by descending last modified, enableds by descending last modified, incapable devices last
        // (note that last modified tie break should not depend on modifier/sortDirection, the sort direction is only enabled first or disabled first)
    };

type LineStatusFields = {
    spotterId: string;
    isTransitioning: boolean;
    shouldBeActive: boolean;
    hasHardware: boolean;
    isAllowed: boolean;
    varSupportsDisabling: boolean;
    isOtaInProgress: boolean;
    lastModified: string | null;
};

const isLikelyTransitioning = (lineType: LineType, subscriptionTags: SubscriptionTagInfo) => {
    const now = new Date(); // plumbing useNow into here doesn't seem worth it, we'll drive refreshes off refetching tags rather than now advancing
    const networkStatus = subscriptionTags[`${lineType}_network_status`];
    if (networkStatus && LINE_TRANSITION_STATES.includes(networkStatus)) {
        // if it says it's transitioning, trust it
        return true;
    }
    if (lineType !== 'iridium') {
        return false;
    }
    // if it's iridium, there's a brief gap between when we initiate the transition and wavefleet updating the status.
    // if the "should" state and the network status disagree, _and_ the "should" state was changed very recently,
    // we probably just haven't gotten the fresh network status yet.
    if (subscriptionTags.should_iridium_be_active === (networkStatus === 'ACTIVATED')) {
        // should and network status agree, definitely not transitioning
        return false;
    }
    if (
        subscriptionTags.should_iridium_be_active_last_modified &&
        differenceInMinutes(now, subscriptionTags.should_iridium_be_active_last_modified) < 2
    ) {
        // if we're within a minute of things going out of sync, call it transitioning
        return true;
    }
    // still weird that we're out of sync, but don't lock the ui in case someone wants to fiddle with it
    return false;
};

export const collectLineFields = (
    lineType: LineType,
    varSupportsDisabling: boolean,
    isOtaInProgress: boolean,
    subscriptionTags: SubscriptionTagInfo,
    device?: Device
): LineStatusFields => {
    return {
        spotterId: subscriptionTags.spotter_id,
        isTransitioning: varSupportsDisabling && isLikelyTransitioning(lineType, subscriptionTags),
        shouldBeActive: subscriptionTags[`should_${lineType}_be_active`],
        lastModified: subscriptionTags[`should_${lineType}_be_active_last_modified`],
        hasHardware:
            subscriptionTags.telemetry_capabilities.includes(lineType) &&
            !(lineType === 'cellular' && device?.cellularData?.hasCellular === false),
        isAllowed:
            subscriptionTags[`is_${lineType}_allowed`] &&
            !(lineType === 'cellular' && device?.cellularData?.cellularEnabled === false),
        varSupportsDisabling,
        isOtaInProgress,
    };
};

type DeviceTelemetryCellProps = {
    editMode: boolean;
    lineType: LineType;
    lineStatus: LineStatusFields;
    pendingChange?: boolean;
};

type TelemetryToggleTooltipOptions = {
    isOtaInProgress: boolean;
    noCreditsAvailable: boolean;
    varSupportsDisabling: boolean;
};

const getTelemetryToggleTooltip = ({
    isOtaInProgress,
    noCreditsAvailable,
    varSupportsDisabling,
}: TelemetryToggleTooltipOptions): string => {
    // An update in progress takes priority, because it blocks every change until it finishes.
    if (isOtaInProgress) {
        return OTA_IN_PROGRESS_TOOLTIP;
    }
    if (noCreditsAvailable) {
        return 'Add satellite credits to enable this satellite line';
    }
    if (!varSupportsDisabling) {
        return 'Satellite lines cannot be disabled';
    }
    return '';
};

const DeviceTelemetryCell = ({ editMode, lineStatus, lineType }: DeviceTelemetryCellProps) => {
    const { data: creditBalance } = useCreditBalance();
    const switchContainerRef = useRef<HTMLButtonElement>(null);
    const {
        spotterId,
        isTransitioning,
        shouldBeActive,
        hasHardware,
        isAllowed,
        varSupportsDisabling,
        isOtaInProgress,
        lastModified,
    } = lineStatus;
    const telemetryLabel = telemetryTypeToLabelMap[lineType];
    const { telemetryLineChanges, toggleTelemetryLine } = useTelemetryLineChanges();
    const pendingChange = telemetryLineChanges[spotterId]?.[lineType];

    if (varSupportsDisabling) {
        /*
         * Various cases that don't support toggling. However, not using these for var-doesn't-support-disabling
         * because in edit mode we render that as a disabled "on" switch, so that's handled as a special case down there.
         */
        if (!hasHardware) {
            return <Typography variant="minorLabel">Not {telemetryLabel} capable</Typography>;
        }
        if (!isAllowed) {
            return <Typography variant="minorLabel">{capitalize(telemetryLabel)} modem disabled</Typography>;
        }
        if (isTransitioning) {
            return (
                <Stack>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                        <PendingIcon color={shouldBeActive ? 'warning' : 'disabled'} sx={{ fontSize: '18px' }} />{' '}
                        <Typography variant="h5">{shouldBeActive ? 'Enabling...' : 'Disabling...'}</Typography>
                    </Stack>
                    <Typography variant="minorLabel">This can take up to 10 minutes</Typography>
                </Stack>
            );
        }
    }

    if (!editMode) {
        if (!shouldBeActive && varSupportsDisabling) {
            return (
                <Stack>
                    <Typography variant="h5">Disabled</Typography>
                    <Typography variant="minorLabel">
                        {lastModified ? (
                            <>
                                Last disabled{' '}
                                <Timestamp value={lastModified} type="medium" unit="date" variant="inherit" />
                            </>
                        ) : (
                            'Never enabled '
                        )}
                    </Typography>
                </Stack>
            );
        }
        return (
            <Stack>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                    <SuccessIcon sx={{ fontSize: '18px' }} />
                    <Typography variant="h5">Enabled</Typography>
                </Stack>
                <Typography variant="minorLabel">
                    Last enabled <Timestamp value={lastModified} type="medium" unit="date" variant="minorLabel" />
                </Typography>
            </Stack>
        );
    }

    /*
     * EDIT MODE
     */

    const checked = !varSupportsDisabling || (pendingChange ?? shouldBeActive);
    // only disable/message on "no credits available" if it's currently deactivated; you're allowed to turn off when out of credits
    // using shouldBeActive rather than checked so that it doesn't disable immediately if you do turn it off
    const noCreditsAvailable =
        !shouldBeActive &&
        lineType === 'iridium' &&
        creditBalance?.data &&
        creditBalance.data.credit_balance <= 0 &&
        varSupportsDisabling;
    const disabled = isOtaInProgress || !varSupportsDisabling || noCreditsAvailable;
    const tooltipText = getTelemetryToggleTooltip({
        isOtaInProgress,
        noCreditsAvailable: Boolean(noCreditsAvailable),
        varSupportsDisabling,
    });

    return (
        <Tooltip
            title={tooltipText}
            slotProps={{
                popper: {
                    anchorEl: () => switchContainerRef.current!,
                },
            }}
        >
            <Box component="div" sx={{ width: 1 }}>
                <FormControlLabel
                    label={<Typography variant="h5">Enable {telemetryLabel} line</Typography>}
                    labelPlacement="start"
                    sx={{ m: 0, justifyContent: 'space-between', width: 1 }}
                    control={
                        <Box ref={switchContainerRef}>
                            <Switch
                                data-testid="toggle-telemetry-switch"
                                onClick={() => toggleTelemetryLine(spotterId, lineType)}
                                checked={checked}
                                disabled={disabled}
                            />
                        </Box>
                    }
                />
            </Box>
        </Tooltip>
    );
};
